// Environment Configuration
// Detects running environment and configures appropriate flow

import { gameLogger } from '../../utils/GameLogger';
import { bootSearchParams } from '../boot/bootUrl';

export type Environment = 'development' | 'production' | 'test';

export interface EnvironmentConfig {
  env: Environment;
  isLocalhost: boolean;
  isProduction: boolean;
  autoDevLogin: boolean;
  skipAuth: boolean;
  baseUrl: string;
  apiUrl: string;
  wsUrl: string;
}

// Production hostnames — any deployed public surface that should never
// auto-login or expose dev tooling. QA tunnel subdomains (*-qa.*) are
// intentionally omitted so dev-fast-tunnel sessions keep auto-login.
const PRODUCTION_HOSTNAMES = new Set([
  'djmsqrvve.com',
  'www.djmsqrvve.com',
  'h2000.djmsqrvve.com',      // VPS live server (prod)
  'h2000-ptr.djmsqrvve.com',  // VPS live server (PTR)
  'helix2000.com',            // public main game domain
  'www.helix2000.com',
  'play.helix.game',          // future public domain
  'helix.game',
]);

// Main game domain — the "unavoidable" public surface that skips the
// HomeModePicker card grid and boots straight into the adventure.
// Both helix2000.com and PTR are `production` environment, but only
// helix2000.com is a main-game domain (PTR keeps the card picker).
const MAIN_GAME_HOSTNAMES = new Set([
  'helix2000.com',
  'www.helix2000.com',
]);

/** Returns true on the public main game domain (helix2000.com). */
export function isMainGameDomain(): boolean {
  if (typeof window === 'undefined') return false;
  return MAIN_GAME_HOSTNAMES.has(window.location.hostname);
}

// Detect environment based on hostname and optional VITE_ENV build flag.
// VITE_ENV=production is baked in at build time for VPS deploys (see
// scripts/deploy/deploy-vps.sh) so any future public hostname is covered
// without requiring a code change.
function detectEnvironment(): Environment {
  if (typeof (globalThis as any).window === 'undefined') {
    return 'development'; // Server-side
  }

  // Build-time override — set VITE_ENV=production in VPS deploy script.
  if (process.env.VITE_ENV === 'production') {
    return 'production';
  }

  const hostname = (globalThis as any).window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  if (PRODUCTION_HOSTNAMES.has(hostname)) {
    return 'production';
  }

  // *.game TLDs are always public game surfaces
  if (hostname.endsWith('.game')) {
    return 'production';
  }

  // Fall through: QA tunnels, staging, local dev — treat as development
  return 'development';
}

// Get environment configuration
export function getEnvironmentConfig(): EnvironmentConfig {
  const env = detectEnvironment();
  const isLocalhost = env === 'development';
  const isProduction = env === 'production';

  // URL `?skipLogin=` opt-out for the auto-login path.
  let urlSkipLogin: string | null = null;
  const g = globalThis as any;

  if (g.window && g.document && g.localStorage) {
    try {
      urlSkipLogin = bootSearchParams().get('skipLogin');
    } catch {
      // localStorage not available
    }
  }

  // Development mode: auto-login unless explicitly disabled by flag, environment variable, or URL parameter
  let skipLoginEnv: string | undefined;
  try {
    const meta = g.import?.meta;
    skipLoginEnv = meta?.env?.VITE_SKIP_LOGIN;
  } catch {
    // import.meta not available
  }

  const autoDevLogin = isLocalhost && urlSkipLogin !== 'false' && skipLoginEnv !== 'false';

  // Env-var overrides. Set at build time (VPS deploy, Cloudflare tunnel) to
  // point the client at a remote host. Vite's `define.process.env = {...}`
  // replaces `process.env.X` expressions directly — the `typeof process`
  // guard that used to wrap this was always false in the browser bundle, so
  // the overrides silently never applied. Accessing `process.env.X` directly
  // works because Vite static-replaces the expression; the try/catch is
  // defensive in case a tool other than Vite serves this file.
  let envWsUrl: string | undefined;
  let envApiUrl: string | undefined;
  try {
    envWsUrl = process.env.VITE_WS_URL;
    envApiUrl = process.env.VITE_API_URL;
  } catch { /* non-Vite host without process.env replacement */ }

  // Origin-derived URLs for the deployed-tier branch. Reading from
  // window.location.origin keeps the bundle host-agnostic — same JS
  // works on h2000-ptr / h2000.djmsqrvve.com / a future tier without
  // a rebuild. The hard-coded 'djmsqrvve.com/helix2000' fallbacks
  // were stale (PTR/prod don't actually serve from /helix2000) and
  // tripped the build-time bundle guard. — Arc A.1 fix 2026-05-02.
  const browserOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';

  // CRITICAL: this gate must resolve to a STATIC value in the
  // production bundle so Vite tree-shakes the localhost branch
  // (scripts/vps/check-bundle.mjs trips on leaked URLs otherwise).
  //
  // The `__H2000_DEV_BUILD__` identifier is statically replaced by
  // Vite via `define` in vite.config.mjs:
  //   - `vite build` → false (production bundle)
  //   - `vite dev`   → true
  //
  // ts-jest's CJS output never sees the identifier (no Vite involvement),
  // so the `typeof` guard falls through to the runtime
  // `process.env.NODE_ENV` path. This sidesteps the TS1343 problem
  // that `import.meta.env.DEV` causes in ts-jest, AND sidesteps the
  // tree-shake problem that `isDevMode()` (a runtime function) caused.
  // Best of both — see feedback_isdevmode_vs_import_meta.md for the
  // prior dance.
  const isDevBuild: boolean =
    typeof __H2000_DEV_BUILD__ !== 'undefined'
      ? __H2000_DEV_BUILD__
      : (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production');

  let baseUrl: string;
  let apiUrl: string;
  let wsUrl: string;

  if (isDevBuild) {
    // In dev builds, use the current page's hostname when available (e.g. Tailscale
    // IP 100.x.y.z or local LAN) so mobile and remote devices connect back to this host.
    // 'localhost' is mapped to '127.0.0.1' to avoid browser IPv6/IPv4 resolution ambiguity;
    // the Colyseus dev server binds 0.0.0.0 on :2567 and Vite serves on :3001.
    const rawHost = typeof window !== 'undefined' && window.location?.hostname
      ? window.location.hostname
      : '127.0.0.1';
    const devHost = rawHost === 'localhost' ? '127.0.0.1' : (rawHost || '127.0.0.1');

    baseUrl = isLocalhost ? `http://${devHost}:3001` : browserOrigin;
    apiUrl = envApiUrl ?? (isLocalhost ? `http://${devHost}:2567/api` : `${browserOrigin}/api`);
    wsUrl = envWsUrl ?? (isLocalhost ? `ws://${devHost}:2567` : browserOrigin.replace(/^http/, 'ws'));
  } else {
    // Production bundle — VITE_* baked in by deploy.sh. browserOrigin
    // is the runtime fallback if VITE_* weren't set (or were set to
    // empty string — the prod deploy script intentionally leaves
    // VITE_API_URL / VITE_WS_URL unset so the same bundle serves both
    // helix2000.com and h2000.djmsqrvve.com via same-origin derivation).
    // `||` (not `??`) so empty string also falls through to the origin.
    baseUrl = browserOrigin;
    apiUrl = envApiUrl || `${browserOrigin}/api`;
    wsUrl = envWsUrl || browserOrigin.replace(/^http/, 'ws');
  }

  return {
    env,
    isLocalhost,
    isProduction,
    autoDevLogin,        // Auto-login on localhost
    skipAuth: autoDevLogin, // Skip auth in dev mode
    baseUrl,
    apiUrl,
    wsUrl
  };
}

// Toggle dev login screen (can be called from console or dev tools)
export function toggleDevLogin(enable: boolean): void {
  const g = globalThis as any;
  if (!g.window || !g.localStorage) {
    gameLogger.warn('environment', 'toggleDevLogin: Not in browser environment');
    return;
  }

  if (enable) {
    g.localStorage.setItem('helix_force_login', 'true');
  } else {
    g.localStorage.removeItem('helix_force_login');
  }
  // Reload to apply changes
  g.window.location.reload();
}

// Check if auto-dev-login is in effect (localhost + skipLogin not opted out).
// NOT a generic "is this a dev build" check — for that, use
// `isDevMode()` from `src/shared/utils/isDevMode.ts` (NODE_ENV) or the
// `__H2000_DEV_BUILD__` Vite-define (build-time, tree-shake-friendly).
// Renamed from `isDevMode` (2026-05-03) to eliminate the name collision
// that previously misled callers into using this as a tree-shake gate.
export function isAutoDevLogin(): boolean {
  const config = getEnvironmentConfig();
  return config.autoDevLogin;
}

// Get appropriate token for environment.
// Phase 3 (gap-closing) — the autoDevLogin path used to return the
// literal 'DEV_TOKEN' magic string. Now it returns whatever JWT lives
// in localStorage; useAuth.ts populates that on first dev visit via
// /api/auth/dev/issue. Empty string when no token is present yet
// (caller decides whether to treat that as "skip auth" or "wait").
export function getEnvironmentToken(): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem('helix_jwt') || '';
}



